import { useEffect, useRef, useState } from 'react';

import type { QltUploadFile } from '../../api/repository/upload';
import { isSubmissionDocumentId, useDynamicDocumentStore } from '../filmstrip/documents';
import { normalizeYoutubeUrl, type YoutubeVideoReference } from '../link/youtube';
import type { UploadViewer } from '../upload/validation';
import { useBoardContext } from '../useBoardContext';
import { useClearUnit, useSelectUnit } from '../useSelectUnit';
import { useSubmissionStore } from './store';
import { answerDocument, fileDocument, itemLabel, submissionItems, type StudentSubmission, type SubmissionItem } from './submissions';

export type SubmissionOverlayState =
  | { kind: 'file'; file: QltUploadFile; viewer: UploadViewer }
  | { kind: 'youtube'; video: YoutubeVideoReference }
  | { kind: 'link'; url: string; title: string };

function overlayFor(submission: StudentSubmission, items: SubmissionItem[], index: number): SubmissionOverlayState | null {
  const item = items[index];
  const title = `${submission.studentName} - ${itemLabel(items, index)}`;
  if (item.kind === 'file') {
    const file = item.file;
    return { kind: 'file', viewer: file.viewer, file: { id: `${submission.id}-${index}`, name: title, mime: file.mime, size: file.size, url: file.url } };
  }
  if (item.kind === 'link') {
    const video = normalizeYoutubeUrl(item.url);
    return video ? { kind: 'youtube', video } : { kind: 'link', url: item.url, title };
  }
  return null;
}

// Puts the navigator's current item on the board: an image or PDF as a document the pen writes on,
// anything else over the submission's painted cover page
export function useSubmissionViewer() {
  const context = useBoardContext();
  const viewer = useSubmissionStore((state) => state.viewer);
  const openViewer = useSubmissionStore((state) => state.open);
  const closeViewer = useSubmissionStore((state) => state.close);
  const selectUnit = useSelectUnit();
  const clearUnit = useClearUnit();
  const [overlay, setOverlay] = useState<SubmissionOverlayState | null>(null);
  // the effect must not re-run on every render, yet navigate with the current route
  const selectRef = useRef(selectUnit);
  selectRef.current = selectUnit;

  const current = viewer ? viewer.order[viewer.index] : null;
  const item = viewer ? viewer.item : 0;
  const tick = viewer ? viewer.tick : -1;

  useEffect(() => {
    if (!current) { setOverlay(null); return; }
    const items = submissionItems(current);
    const index = Math.min(item, items.length - 1);
    const shown = items[index];
    const page = shown.kind === 'file' ? fileDocument(current, shown.file, index) : null;
    const document = useDynamicDocumentStore.getState().openTransient(page || answerDocument(current));
    selectRef.current({ unitId: document.id, unitName: document.title });
    setOverlay(page ? null : overlayFor(current, items, index));
  }, [current, item, tick]);

  // the viewer belongs to one period's board; leaving it must not carry a class's work into the next
  useEffect(() => () => { useSubmissionStore.getState().close(); }, [context.classId, context.date, context.index]);

  function start(taskTitle: string, order: StudentSubmission[], index: number) {
    // a fresh session drops the last one's pages; the ink stays keyed by homework id
    if (!useSubmissionStore.getState().viewer) useDynamicDocumentStore.getState().clearTransient();
    openViewer(taskTitle, order, index, { unitId: context.unitId, title: context.lessonTitle, courseId: context.courseId });
  }

  function close() {
    const closed = closeViewer();
    setOverlay(null);
    // a lecture the teacher picked meanwhile stays; only a submission page gives way to the lesson
    if (!closed || !isSubmissionDocumentId(context.unitId)) return;
    const origin = closed.origin;
    if (origin.unitId && !isSubmissionDocumentId(origin.unitId)) selectUnit({ unitId: origin.unitId, unitName: origin.title || '', courseId: origin.courseId });
    else clearUnit();
  }

  return { active: Boolean(viewer), overlay, dismissOverlay: () => setOverlay(null), start, close };
}
